import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Radio,
  Input,
  Select,
  Switch,
  Drawer,
  List,
  Space,
  Table,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { IPreAuthQuery } from "../../../interfaces/IPreAuthQuery";
import { addQuery, fetchQueries } from "../../../models/preAuthQuerySlice";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { getDocuments } from "../../../apis/schemeService";

type RequiredDocument = {
  id: any;
  name: string;
  expiryDate: string;
};

type DocumentInfo = {
  id: string;
  value: string;
};

type AddPreAuthQueryModalProps = {
  visible: boolean;
  onCancel: () => void;
  portalMetaDataId: number;
  onfetchStepper: () => void;
};

const { TextArea } = Input;
const { Option } = Select;

const AddPreAuthQueryModal: React.FC<AddPreAuthQueryModalProps> = ({
  visible,
  onCancel,
  portalMetaDataId,
  onfetchStepper,
}) => {
  const { patients, auth, hospital, preAuth } = useSelector(
    (state: RootState) => state
  );
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [selectedQueryStage, setSelectedQueryStage] = useState<any>(undefined);
  const [queryText, setQueryText] = useState<string>("");
  const [typeOfQuery, setTypeOfQuery] = useState<string>("SCHEME QUERY");
  const [assignToFrontOffice, setAssignToFrontOffice] = useState<string>("");
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const [fetchedDocuments, setFetchedDocuments] = useState<DocumentInfo[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentInfo[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  // const [filteredDocuments, setFilteredDocuments] = useState([]);
  const dispatch = useDispatch<AppDispatch>();
  // const [documents] = useState<string[]>([
  //   "Claim Query Reply",
  //   "POD",
  //   "Patient Cash Receipt",
  //   "Discharge Summary with Sign and Seal",
  //   "Clear Image of Bill",
  //   "Preauth Doc",
  //   "Preauth Document",
  //   "Preauth Form",
  //   "Implant - Invoice",
  //   "Ultra Sound",
  //   "Pan Card",
  //   "Spiral CT",
  //   "Blood Transfusion Sticker",
  //   "Angio Gram Still Image",
  // ]);

  const schemeSchemaKeys = [
    "casePreAuthCghs",
    "casePreAuthEchs",
    "casePreAuthIhx",
    "casePreAuthStarhealth",
    "casePreAuthVidal",
  ];

  const schemePortalData = schemeSchemaKeys.reduce((result: any, key) => {
    if (preAuth?.portalData?.[key] && key !== "casePreAuthEchs") {
      result = preAuth?.portalData?.[key];
    }
    else if(key === "casePreAuthEchs" && preAuth?.portalData?.[key]) {
      if(preAuth?.portalData?.[key]?.casePreAuthEchsReferralResponse) {
        result = preAuth?.portalData?.[key]?.casePreAuthEchsReferralResponse;
      }
      else if(preAuth?.portalData?.[key]?.casePreAuthEchsEmergencyResponse) {
        result = preAuth?.portalData?.[key]?.casePreAuthEchsEmergencyResponse;
      }
    }
    return result;
  }, {});

  const casePreAuthId = schemePortalData?.casePreAuthResponse?.id || null;
  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

  useEffect(() => {
    if (portalMetaDataId && schemeMetaDataId) {
      const fetchDocuments = async () => {
        // setLoading(true);
        try {
          const data = await getDocuments(portalMetaDataId, schemeMetaDataId);
          const uniqueDocuments = Object.values(data)
            .flat()
            ?.filter(
              (doc: any, index: number, self: any[]) =>
                index === self?.findIndex((t: any) => t.label === doc.label)
            )
            ?.map((doc: any) => ({ id: doc.value, value: doc.label }));
          setFetchedDocuments(uniqueDocuments);
          // setFilteredDocuments(uniqueDocuments);
        } catch (err) {
          message.error("Failed to fetch documents.");
        }
      };

      fetchDocuments();
    }
  }, [portalMetaDataId, schemeMetaDataId]);

  const showDrawer = (): void => {
    setIsDrawerVisible(true);
  };

  const addDocument = (document: DocumentInfo): void => {
    if (!requiredDocuments.some((doc) => doc.name === document.value)) {
      const newDoc: RequiredDocument = {
        id: document.id, // Use the document's ID
        name: document.value,
        expiryDate: "",
      };
      setRequiredDocuments([...requiredDocuments, newDoc]);
    }
  };

  const removeDocument = (name: string): void => {
    setRequiredDocuments(requiredDocuments?.filter((doc) => doc.name !== name));
  };

  const updateExpiryDate = (id: number | string, expiryDate: string): void => {
    setRequiredDocuments(
      requiredDocuments?.map((doc) =>
        doc.id === id ? { ...doc, expiryDate } : doc
      )
    );
  };

  const handleModal = (): void => {
    setIsModalVisible(false);
  };

  const handleAddQuery = () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    if (caseId && visitId) {
      const queryData: IPreAuthQuery = {
        queryStage: selectedQueryStage, // assuming you'll add this state
        stageId: 0, // you may want to get this from somewhere
        queryQuestion: queryText, // from TextArea
        typeOfQuery: typeOfQuery, // from Radio.Group
        assignToFrontOffice: assignToFrontOffice === "YES" ? true : false, // from Radio.Group
        requiredDocuments: requiredDocuments, // directly use the existing state,
        casePreAuthId: casePreAuthId,
        queryType: "Manual"
      };

      dispatch(addQuery({ queryData, caseId, visitId, portalMetaDataId }))
        .then((result) => {
          if (addQuery.fulfilled.match(result)) {
            setQueryText("");
            setSelectedQueryStage("");
            setAssignToFrontOffice("");
            onCancel(); // Close the modal
            // Optional: Reset form states
            onfetchStepper();
            setRequiredDocuments([]);
            // dispatch(fetchQueries({ caseId, visitId, portalMetaDataId }));
          }
        })
        .catch((error) => {
          // Handle error
          console.error("Failed to add query", error);
        });
    }
  };
  const handleSearch = (value: string): void => {
    const filtered = fetchedDocuments?.filter((doc) =>
      doc.value.toLowerCase().startsWith(value.toLowerCase())
    );
    setFilteredDocuments(filtered);
  };

  const drawerColumns = [
    {
      title: "Document Name",
      dataIndex: "value",
      key: "value",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Add/Remove",
      key: "action",
      render: (_: any, record: DocumentInfo) => (
        <Space>
          {requiredDocuments.some((d) => d.name === record.value) ? (
            <Button
              type="text"
              icon={<MinusOutlined />}
              onClick={() => removeDocument(record.value)}
            />
          ) : (
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => addDocument(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (searchTerm.length >= 4) {
      const filtered = fetchedDocuments?.filter((doc) =>
        // Adjust this filter based on which document properties you want to search
        doc.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDocuments(filtered);
    } else {
      setFilteredDocuments(fetchedDocuments);
    }
  }, [searchTerm, fetchedDocuments]);
  return (
    <div style={{ padding: "16px" }}>
      <Modal
        // title="Add Query"
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={700}
        centered
        bodyStyle={{ padding: 20 }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            // padding: "23px",
            // borderRadius: "8px",
            // border: "1px solid #D9D9D9",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <label style={{ width: "30%", fontWeight: "600" }}>
              Query Stage:
            </label>
            <Select
              // style={{ flex: 1 }}
              style={{
                width: "100%",
                border: "none",
                boxShadow: "none",
                flex: 1,
              }}
              placeholder="Select"
              // defaultActiveFirstOption={true}
              value={selectedQueryStage}
              onChange={(value) => setSelectedQueryStage(value)}
            >
              <Option value="preAuth">Pre Auth</Option>
              <Option value="enhancement">Enhancement</Option>
              <Option value="discharge">Discharge</Option>
              <Option value="claim">Claim</Option>
            </Select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <label style={{ width: "30%", fontWeight: "600" }}>
              Type of Query:
            </label>
            <Radio.Group
              defaultValue="SCHEME QUERY"
              value={typeOfQuery}
              onChange={(e) => setTypeOfQuery(e.target.value)}
            >
              <Radio
                value="SCHEME QUERY"
                className={`custom-radio ${
                  typeOfQuery === "SCHEME QUERY" ? "selected" : ""
                }`}
              >
                Scheme Query
              </Radio>
              <Radio
                value="BACK OFFICE QUERY"
                className={`custom-radio ${
                  typeOfQuery === "BACK OFFICE QUERY" ? "selected" : ""
                }`}
              >
                Back Office Query
              </Radio>
            </Radio.Group>
          </div>
          <label style={{ width: "30%", fontWeight: "600" }}>Query:</label>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <TextArea
              rows={5}
              placeholder="Enter your query..."
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
            />
          </div>

          <div>
            <Button
              type="primary"
              style={{
                backgroundColor: "#008B8B",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
              onClick={showDrawer}
            >
              Request Documents
            </Button>
          </div>

          <div
            style={{
              maxHeight: "250px", // Set a maximum height for 5 items (adjust as needed)
              overflowY: "auto", // Enable vertical scrolling
              border:
                requiredDocuments.length > 0 ? "1px solid #d9d9d9" : "none",

              paddingBottom: "19px",
              paddingTop: "12px",
              paddingLeft: "10px",
              paddingRight: "10px",
            }}
          >
            {requiredDocuments?.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 0",
                  // borderBottom: "1px solid #d9d9d9",
                  paddingLeft: "20px",
                }}
              >
                <span style={{ flex: 2, fontWeight: "600" }}>{doc.name}</span>
                <Select
                  style={{ width: "40%" }}
                  placeholder="Select Expiry"
                  onChange={(value) => updateExpiryDate(doc.id, value)}
                >
                  <Option value="30min">Within 30 Minutes</Option>
                  <Option value="1hr">Within 1 Hour</Option>
                  <Option value="2hr">Within 2 Hour</Option>
                  <Option value="5h">Within 5 Hour</Option>
                  <Option value="8h">Within 8 Hour</Option>
                  <Option value="24h">Within 24 Hour</Option>
                </Select>
                <Button
                  type="text"
                  icon={<MinusOutlined />}
                  onClick={() => removeDocument(doc.name)}
                />
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <label style={{ width: "30%", fontWeight: "600" }}>
              Assign to Front Desk:
            </label>
            <Radio.Group
              defaultValue="YES"
              value={assignToFrontOffice}
              onChange={(e) => setAssignToFrontOffice(e.target.value)}
            >
              <Radio
                value="YES"
                className={`custom-radio ${
                  assignToFrontOffice === "YES" ? "selected" : ""
                }`}
              >
                Yes
              </Radio>
              <Radio
                value="NO"
                className={`custom-radio ${
                  assignToFrontOffice === "NO" ? "selected" : ""
                }`}
              >
                No
              </Radio>
            </Radio.Group>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              marginTop: "20px",
            }}
          >
            <Button
              onClick={onCancel}
              style={{
                borderColor: "#D2691E",
                color: "#FB923C",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              style={{
                backgroundColor: "#D2691E",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "30%",
              }}
              onClick={handleAddQuery}
            >
              Add Query
            </Button>
          </div>
        </div>
      </Modal>

      <Drawer
        title="Documents List"
        placement="right"
        open={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        width={400}
      >
        <Input.Search
          placeholder="Search Documents"
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
          allowClear
        />
        <Table
          dataSource={filteredDocuments}
          rowKey="id"
          columns={drawerColumns}
          pagination={false}
          onRow={(record, index: any) => ({
            style: {
              backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
            },
          })}
        />
      </Drawer>
    </div>
  );
};

export default AddPreAuthQueryModal;
